const MAX_MS = 60 * 60 * 1000;
/** 말소리 기준. 32kbps면 60분이 약 14MB라 20MB 안에 들어간다(브라우저가 값을 낮추거나 높일 수 있어 용량 제한도 둔다). */
const AUDIO_BPS = 32_000;
/** 멈춘 뒤 마지막 조각이 더 붙어도 20MB를 넘지 않게 여유를 둔다. */
const SIZE_LIMIT = Math.floor(19.5 * 1024 * 1024);
/** 스스로 멈춘 녹음기의 stop 이벤트가 오지 않을 때 기다리는 시간 */
const STOP_FALLBACK_MS = 1000;

/** 기기 안에서만 녹음한다. 외부 전송·자동 영구 저장 없음. */
export class LocalRecorder {
  constructor({ mediaDevices = globalThis.navigator?.mediaDevices, Recorder = globalThis.MediaRecorder, onState = () => {}, onError = () => {} } = {}) {
    Object.assign(this, { mediaDevices, Recorder, onState, onError });
    this.state = 'idle'; this.parts = []; this.bytes = 0; this.limit = SIZE_LIMIT; this.remainingMs = MAX_MS;
  }
  async start(consented) {
    if (!consented) throw new Error('녹음 안내와 동의 상태를 확인하세요.');
    if (this.state !== 'idle') throw new Error('현재 녹음을 먼저 종료하세요.');
    if (!this.mediaDevices?.getUserMedia || !this.Recorder) throw new Error('이 브라우저는 녹음을 지원하지 않습니다. HTTPS 또는 localhost에서 열어 주세요.');
    this.setState('starting'); this.cancelled = false; this.discard = false;
    try {
      const stream = await this.mediaDevices.getUserMedia({ audio: true });
      if (this.cancelled) { stream.getTracks().forEach(t => t.stop()); this.setState('idle'); return; }
      this.stream = stream;
      const mimeType = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/ogg;codecs=opus'].find(t => this.Recorder.isTypeSupported(t));
      this.recorder = new this.Recorder(stream, { audioBitsPerSecond: AUDIO_BPS, ...(mimeType ? { mimeType } : {}) });
      this.parts = []; this.bytes = 0;
      this.recorder.ondataavailable = e => {
        if (e.data?.size) { this.parts.push(e.data); this.bytes += e.data.size; }
        if (this.bytes > this.limit) this.interrupt('20MB에 도달해 녹음을 종료했습니다. 파일을 내려받아 보관하세요.');
      };
      this.recorder.onerror = () => this.interrupt('녹음이 중단되었습니다. 확보된 파일을 확인하세요.');
      this.recorder.onstop = () => this.finish();
      // 직접 멈춘 트랙은 ended 가 오지 않으므로, 이 알림은 마이크가 빠진 경우에만 온다.
      stream.getTracks().forEach(t => { t.onended = () => this.interrupt('마이크 연결이 끊겨 녹음을 종료했습니다. 확보된 파일을 확인하세요.'); });
      this.recorder.start(1000); this.setState('recording');
      this.remainingMs = MAX_MS; this.armTimer();
    } catch (e) { this.stream?.getTracks().forEach(t => t.stop()); this.setState('idle'); throw e; }
  }
  /** 일시정지한 시간은 60분에 넣지 않는다. */
  armTimer() {
    this.timerStartedAt = Date.now();
    this.timer = setTimeout(() => this.interrupt('최대 60분에 도달해 종료했습니다.'), this.remainingMs);
  }
  pause() {
    if (this.state !== 'recording') return;
    clearTimeout(this.timer); this.remainingMs -= Date.now() - this.timerStartedAt;
    this.recorder.pause(); this.setState('paused');
  }
  resume() { if (this.state === 'paused') { this.recorder.resume(); this.setState('recording'); this.armTimer(); } }
  interrupt(message) {
    if (!['recording', 'paused'].includes(this.state)) return;
    this.onError(new Error(message)); this.stop().catch(this.onError);
  }
  stop() {
    if (this.stopPromise && this.state === 'stopping') return this.stopPromise;
    if (!['recording', 'paused'].includes(this.state)) return Promise.reject(new Error('진행 중인 녹음이 없습니다.'));
    clearTimeout(this.timer); this.setState('stopping');
    this.stopPromise = new Promise(resolve => { this.resolveStop = resolve; });
    // 오류·마이크 끊김으로 이미 멈췄다면 stop 이벤트를 잠시 기다린다. 오지 않아도 확보한 파일을 돌려준다.
    if (this.recorder.state === 'inactive') this.fallback = setTimeout(() => this.finish(), STOP_FALLBACK_MS);
    else this.recorder.stop();
    return this.stopPromise;
  }
  finish() {
    if (this.state === 'idle') return;
    clearTimeout(this.timer); clearTimeout(this.fallback);
    const blob = new Blob(this.parts, { type: this.recorder.mimeType || 'audio/webm' });
    this.stream?.getTracks().forEach(t => t.stop());
    this.setState('idle'); this.resolveStop?.(blob);
    if (!this.discard) this.onBlob?.(blob);
  }
  /** 화면을 닫거나 시작을 취소할 때. 이번 녹음만 버리고, 다음 녹음은 정상 동작한다. */
  dispose() {
    this.cancelled = true; this.discard = true; clearTimeout(this.timer);
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.stream?.getTracks().forEach(t => t.stop());
  }
  /** 화면이 사라질 때. 녹음을 버리고 이후 알림도 보내지 않는다. */
  close() { this.dispose(); this.onState = () => {}; this.onError = () => {}; }
  setState(state) { this.state = state; this.onState(state); }
}
